import { DocumentTemplate } from "@dune/types";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	Button,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "@dune/ui";
import { Play, Plus, RotateCcw, Trash2, Upload, Download } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import {
	useDeleteAllDocumentTemplateVersions,
	useRevertDocumentTemplateVersion,
} from "../../../../../api/mutations/documents.mutations";
import {
	getSubtypeFromTemplateType,
	getTemplateBaseType,
} from "../../../../../utils/documentTemplateUtils";
import {
	getDocumentUrl,
	getFileWithMetadata,
} from "../../../../../api/queries/files.queries";
import TestGenerationDialog from "../TestGenerationDialog";
import TemplatesTabSkeleton from "../skeletons/TemplatesTabSkeleton";
import { downloadFileWithName } from "../../../../../utils/document.utils";

interface TemplatesTabContentProps {
	isLoading: boolean;
	error?: Error | null;
	templates?: DocumentTemplate[];
	openUpload: (id: string) => void;
	onCreateTemplate: () => void;
}

const TemplatesTabContent: React.FC<TemplatesTabContentProps> = ({
	isLoading,
	error,
	templates,
	openUpload,
	onCreateTemplate,
}) => {
	const revertVersion = useRevertDocumentTemplateVersion();
	const deleteAllVersions = useDeleteAllDocumentTemplateVersions();
	const [testTemplate, setTestTemplate] = useState<DocumentTemplate | null>(
		null,
	);

	const handleRevert = (id: string) => {
		revertVersion.mutate(id, {
			onSuccess: () => {
				toast.success("Version précédente restaurée");
			},
			onError: (err) => {
				toast.error(`Échec de la restauration : ${err.message}`);
			},
		});
	};

	const handleDeleteAll = (id: string) => {
		deleteAllVersions.mutate(id, {
			onSuccess: () => {
				toast.success("Modèle supprimé");
			},
			onError: (err) => {
				toast.error(`Échec de la suppression : ${err.message}`);
			},
		});
	};

	const downloadTemplate = async (id: string) => {
		const template = templates?.find((t) => t.id === id);
		if (!template || !template.fileId) {
			toast.error("Impossible de trouver le fichier du modèle");
			return;
		}

		try {
			const fileMetadata = await getFileWithMetadata(template.fileId);
			const downloadUrl = await getDocumentUrl({
				bucket: fileMetadata.bucket || "private",
				key: fileMetadata.key,
			});
			await downloadFileWithName(downloadUrl, template.name);
		} catch (_err) {
			toast.error("Erreur lors de la récupération du lien de téléchargement");
		}
	};

	if (isLoading) {
		return <TemplatesTabSkeleton />;
	}

	if (error) {
		return <p className="text-red-500">Erreur : {error.message}</p>;
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-2xl font-semibold text-foreground">
						Modèles de documents
					</h2>
					<p className="text-muted-foreground mt-1">
						Gérez vos modèles de documents avec des champs dynamiques
					</p>
				</div>
				<Button onClick={onCreateTemplate} className="flex items-center gap-2">
					<Plus className="w-4 h-4" />
					Créer un nouveau modèle
				</Button>
			</div>

			{templates && templates.length > 0 ? (
				<div className="border rounded-lg">
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Nom</TableHead>
								<TableHead>Modèle d'entité</TableHead>
								<TableHead>Type de template</TableHead>
								<TableHead>Sous-type</TableHead>
								<TableHead>Version</TableHead>
								<TableHead>Statut</TableHead>
								<TableHead>Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{templates?.map((template: DocumentTemplate) => (
								<TableRow key={template.id}>
									<TableCell>{template.name}</TableCell>
									<TableCell>
										<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
											{template.entityModel}
										</span>
									</TableCell>
									<TableCell>
										<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
											{getTemplateBaseType(template.templateType)}
										</span>
									</TableCell>
									<TableCell>
										{getSubtypeFromTemplateType(template.templateType) ? (
											<span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
												{getSubtypeFromTemplateType(template.templateType)}
											</span>
										) : (
											<span className="text-gray-400 text-xs">-</span>
										)}
									</TableCell>
									<TableCell>{template.version}</TableCell>
									<TableCell className="space-x-2">
										{template.validated ? (
											<span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded">
												Validé
											</span>
										) : (
											<Tooltip>
												<TooltipTrigger asChild>
													<span className="bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-0.5 rounded">
														{(template.invalidFields ?? []).length} invalides
													</span>
												</TooltipTrigger>
												<TooltipContent>
													<div className="flex flex-col gap-1">
														{(template.invalidFields ?? []).length === 0 ? (
															<span>Aucune donnée manquante</span>
														) : (
															(template.invalidFields ?? []).map((field) => (
																<span key={field}>{field}</span>
															))
														)}
													</div>
												</TooltipContent>
											</Tooltip>
										)}
										<Tooltip>
											<TooltipTrigger asChild>
												<span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2 py-0.5 rounded">
													{template.fields.length} trouvés
												</span>
											</TooltipTrigger>
											<TooltipContent>
												<div className="flex flex-col gap-1">
													{template.fields.length === 0 ? (
														<span>Aucun champ trouvé</span>
													) : (
														template.fields.map((field) => (
															<span key={field}>{field}</span>
														))
													)}
												</div>
											</TooltipContent>
										</Tooltip>
									</TableCell>
									<TableCell className="space-x-2">
										<Button
											variant="ghost"
											size="icon"
											onClick={() => setTestTemplate(template)}
											title="Tester la génération"
											disabled={!template.validated}
										>
											<Play className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											onClick={() => openUpload(template.id)}
											title="Téléverser une version"
										>
											<Upload className="h-4 w-4" />
										</Button>
										<Button
											variant="ghost"
											size="icon"
											onClick={() => downloadTemplate(template.id)}
											title="Télécharger le modèle"
										>
											<Download className="h-4 w-4" />
										</Button>
										<AlertDialog>
											<AlertDialogTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													disabled={template.version === 1}
													title="Restaurer dernière version"
												>
													<RotateCcw className="h-4 w-4" />
												</Button>
											</AlertDialogTrigger>
											<AlertDialogContent>
												<AlertDialogHeader>
													<AlertDialogTitle>
														Confirmer la restauration
													</AlertDialogTitle>
													<AlertDialogDescription>
														Êtes-vous sûr de vouloir restaurer la version
														précédente ?
													</AlertDialogDescription>
												</AlertDialogHeader>
												<AlertDialogFooter>
													<AlertDialogCancel>Annuler</AlertDialogCancel>
													<AlertDialogAction asChild>
														<Button
															variant="outline"
															onClick={(e) => {
																e.preventDefault();
																handleRevert(template.id);
															}}
															disabled={revertVersion.isPending}
															isLoading={revertVersion.isPending}
														>
															Restaurer
														</Button>
													</AlertDialogAction>
												</AlertDialogFooter>
											</AlertDialogContent>
										</AlertDialog>
										<AlertDialog>
											<AlertDialogTrigger asChild>
												<Button
													variant="ghost"
													size="icon"
													title="Supprimer le modèle"
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</AlertDialogTrigger>
											<AlertDialogContent>
												<AlertDialogHeader>
													<AlertDialogTitle>
														Confirmer la suppression
													</AlertDialogTitle>
													<AlertDialogDescription>
														Êtes-vous sûr de vouloir supprimer toutes les
														versions ?
													</AlertDialogDescription>
												</AlertDialogHeader>
												<AlertDialogFooter>
													<AlertDialogCancel>Annuler</AlertDialogCancel>
													<AlertDialogAction asChild>
														<Button
															variant="destructive"
															onClick={(e) => {
																e.preventDefault();
																handleDeleteAll(template.id);
															}}
															disabled={deleteAllVersions.isPending}
															isLoading={deleteAllVersions.isPending}
														>
															Supprimer
														</Button>
													</AlertDialogAction>
												</AlertDialogFooter>
											</AlertDialogContent>
										</AlertDialog>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</div>
			) : (
				<>
					<div className="text-center py-12">
						<div className="mx-auto w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-4">
							<Plus className="w-12 h-12 text-muted-foreground" />
						</div>
						<h3 className="text-lg font-medium text-foreground mb-2">
							Aucun modèle de document
						</h3>
						<p className="text-muted-foreground">
							Commencez par créer votre premier modèle de document.
						</p>
					</div>
					<div className="flex justify-center">
						<Button
							onClick={onCreateTemplate}
							className="flex items-center gap-2"
						>
							<Plus className="w-4 h-4" />
							Créer un nouveau modèle
						</Button>
					</div>
				</>
			)}
			{testTemplate && (
				<TestGenerationDialog
					open={!!testTemplate}
					onOpenChange={(open) => !open && setTestTemplate(null)}
					template={testTemplate}
				/>
			)}
		</div>
	);
};

export default TemplatesTabContent;
